import { useMemo } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { phaseColorMap } from "@/lib/mockData";
import TaskBlock from "./TaskBlock";
import MilestoneMarker from "./MilestoneMarker";

const TOTAL_DAYS = 90;
const ROW_HEIGHT = 36;
const ROW_GAP = 8;
const LANE_PAD_Y = 12;

const dayToPercent = (day) => ((day - 1) / TOTAL_DAYS) * 100;
const daysToPercent = (days) => (days / TOTAL_DAYS) * 100;

// Greedy interval-packing: assign each task to the lowest non-overlapping row.
function packRows(tasks) {
  const sorted = [...tasks].sort((a, b) => a.startDay - b.startDay);
  const rows = [];
  for (const t of sorted) {
    const tEnd = t.startDay + t.durationDays;
    let placed = false;
    for (const row of rows) {
      const last = row[row.length - 1];
      if (last.startDay + last.durationDays <= t.startDay) {
        row.push(t);
        placed = true;
        break;
      }
    }
    if (!placed) rows.push([t]);
    void tEnd; // touch, keeps shape easy to extend
  }
  return rows;
}

export default function TimelineCanvas({ roadmap }) {
  const { phases, tasks, milestones, currentDay } = roadmap;

  const lanes = useMemo(
    () =>
      phases.map((phase) => {
        const phaseTasks = tasks.filter((t) => t.phaseId === phase.id);
        const rows = packRows(phaseTasks);
        const heightPx =
          rows.length * ROW_HEIGHT +
          Math.max(0, rows.length - 1) * ROW_GAP +
          LANE_PAD_Y * 2;
        return { phase, rows, heightPx };
      }),
    [phases, tasks],
  );

  const weeks = useMemo(
    () =>
      Array.from({ length: 13 }, (_, i) => {
        const startDay = i * 7 + 1;
        const endDay = Math.min((i + 1) * 7, TOTAL_DAYS);
        return { num: i + 1, startDay, endDay };
      }),
    [],
  );

  const todayLeft = `${dayToPercent(currentDay)}%`;

  return (
    <TooltipProvider delayDuration={200}>
      <div
        data-testid="timeline-canvas"
        className="card-soft overflow-x-auto overflow-y-hidden rounded-[28px] p-6"
      >
        <div className="min-w-[960px]">
          <div
            className="relative grid"
            style={{ gridTemplateColumns: "140px 1fr", columnGap: 16 }}
          >
            {/* Milestones row */}
            <div className="flex items-center pr-2">
              <div className="eyebrow">Milestones</div>
            </div>
            <div
              data-testid="milestones-track"
              className="relative"
              style={{ height: 44 }}
            >
              {milestones.map((m) => (
                <MilestoneMarker
                  key={m.id}
                  milestone={m}
                  leftPercent={dayToPercent(m.day)}
                />
              ))}
            </div>

            {/* Phase bands row */}
            <div />
            <div className="relative" style={{ height: 26 }}>
              {phases.map((p) => {
                const left = dayToPercent(p.startDay);
                const width = daysToPercent(p.endDay - p.startDay + 1);
                const c = phaseColorMap[p.color];
                return (
                  <div
                    key={p.id}
                    data-testid={`phase-band-${p.id}`}
                    className="absolute top-1.5 flex items-center overflow-hidden rounded-md px-2.5 text-[10px] font-bold uppercase tracking-[0.14em]"
                    style={{
                      left: `${left}%`,
                      width: `calc(${width}% - 2px)`,
                      height: 18,
                      backgroundColor: c.band,
                      color: c.solid,
                    }}
                  >
                    Phase {phases.indexOf(p) + 1} · {p.name}
                  </div>
                );
              })}
            </div>

            {/* X axis */}
            <div />
            <div className="relative" style={{ height: 28 }}>
              {/* week gridlines */}
              {weeks.slice(1).map((w) => (
                <div
                  key={`grid-${w.num}`}
                  className="absolute top-0 h-full w-px bg-black/[0.06]"
                  style={{ left: `${dayToPercent(w.startDay)}%` }}
                />
              ))}
              {/* week labels */}
              {weeks.map((w) => {
                const center = (w.startDay + w.endDay) / 2;
                return (
                  <div
                    key={`lbl-${w.num}`}
                    data-testid={`week-label-W${w.num}`}
                    className="absolute top-2 -translate-x-1/2 text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500"
                    style={{ left: `${dayToPercent(center)}%` }}
                  >
                    W{w.num}
                  </div>
                );
              })}
              {/* baseline */}
              <div className="absolute bottom-0 left-0 right-0 h-px bg-black/[0.08]" />
            </div>

            {/* Lanes */}
            {lanes.map((lane) => {
              const c = phaseColorMap[lane.phase.color];
              return (
                <div key={`label-${lane.phase.id}`} className="contents">
                  <div
                    className="flex flex-col justify-center pr-2"
                    style={{ height: lane.heightPx }}
                  >
                    <div
                      className="text-[10px] font-bold uppercase tracking-[0.18em]"
                      style={{ color: c.solid }}
                    >
                      {lane.phase.name}
                    </div>
                    <div className="mt-0.5 text-[11px] text-zinc-500">
                      {lane.phase.weeks}
                    </div>
                    <div className="mt-1 text-[10px] font-semibold text-zinc-400">
                      {tasks.filter((t) => t.phaseId === lane.phase.id).length} tasks
                    </div>
                  </div>
                  <div
                    data-testid={`lane-${lane.phase.id}`}
                    className="relative border-t border-black/[0.04]"
                    style={{
                      height: lane.heightPx,
                      backgroundColor: c.soft,
                    }}
                  >
                    {/* week gridlines inside lane */}
                    {weeks.slice(1).map((w) => (
                      <div
                        key={`lane-grid-${lane.phase.id}-${w.num}`}
                        className="absolute top-0 h-full w-px bg-black/[0.04]"
                        style={{ left: `${dayToPercent(w.startDay)}%` }}
                      />
                    ))}
                    {/* tasks */}
                    {lane.rows.map((row, rowIdx) =>
                      row.map((t) => (
                        <TaskBlock
                          key={t.id}
                          task={t}
                          phaseColor={c}
                          left={dayToPercent(t.startDay)}
                          width={daysToPercent(t.durationDays)}
                          top={LANE_PAD_Y + rowIdx * (ROW_HEIGHT + ROW_GAP)}
                          height={ROW_HEIGHT}
                        />
                      )),
                    )}
                  </div>
                </div>
              );
            })}

            {/* Today line — absolute over column 2 */}
            <div
              data-testid="today-line"
              className="pointer-events-none absolute"
              style={{
                top: 0,
                bottom: 0,
                left: `calc(140px + 16px + (100% - 140px - 16px) * ${(currentDay - 1) / TOTAL_DAYS})`,
                width: 0,
              }}
            >
              <div className="absolute -left-px top-12 h-[calc(100%-3rem)] w-[2px] bg-zinc-900/80" />
              <div
                data-testid="today-marker-pill"
                className="absolute -translate-x-1/2 rounded-full bg-zinc-900 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white shadow-md"
                style={{ top: 0 }}
              >
                Today · Day {currentDay}
              </div>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
